/**
 * Quiz Cache Service - Caches quiz data in localStorage and Supabase database
 * Allows users to take quizzes offline and sync when online
 */

import type { QuizData } from '@/types';
import { supabase } from '@/config/supabase';

const QUIZ_STORAGE_KEY = 'nanolez_quiz_cache';
const QUIZ_TABLE = 'article_quizzes';

interface CachedQuiz {
  articleId: string;
  articleTitle: string;
  quiz: QuizData;
  cachedAt: string;
  sourceRoadmapId?: string;
  sourceRoadmapTitle?: string;
}

/**
 * Save quiz to localStorage and database
 */
export const cacheQuiz = async (
  articleId: string,
  articleTitle: string,
  quiz: QuizData,
  sourceRoadmapId?: string,
  sourceRoadmapTitle?: string
): Promise<void> => {
  try {
    const cachedQuiz: CachedQuiz = {
      articleId,
      articleTitle,
      quiz,
      cachedAt: new Date().toISOString(),
      sourceRoadmapId,
      sourceRoadmapTitle
    };

    // Save to localStorage
    const quizCache = getLocalQuizCache();
    const existingIndex = quizCache.findIndex(q => q.articleId === articleId);
    
    if (existingIndex >= 0) {
      quizCache[existingIndex] = cachedQuiz;
    } else {
      quizCache.push(cachedQuiz);
    }
    
    localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(quizCache));
    console.log('✅ Quiz cached in localStorage:', articleId);

    // Try to sync to database
    try {
      await syncQuizToDatabase(articleId, articleTitle, quiz, sourceRoadmapId, sourceRoadmapTitle);
    } catch (dbError) {
      console.warn('⚠️  Failed to sync quiz to database (will retry later):', dbError);
      // Quiz is still saved locally, so continue
    }
  } catch (error) {
    console.error('❌ Error caching quiz:', error);
  }
};

/**
 * Get quiz from cache (localStorage first, then database)
 */
export const getCachedQuiz = async (articleId: string): Promise<QuizData | null> => {
  try {
    // Check localStorage first
    const localQuiz = getLocalQuiz(articleId);
    if (localQuiz) {
      console.log('✅ Quiz loaded from localStorage:', articleId);
      return localQuiz;
    }

    // Try database
    try {
      const quiz = await getQuizFromDatabase(articleId);
      if (quiz) {
        console.log('✅ Quiz loaded from database:', articleId);
        // Cache it locally for future use
        const quizCache = getLocalQuizCache();
        if (!quizCache.find(q => q.articleId === articleId)) {
          quizCache.push({
            articleId,
            articleTitle: '',
            quiz,
            cachedAt: new Date().toISOString()
          });
          localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(quizCache));
        }
        return quiz;
      }
    } catch (dbError) {
      console.warn('⚠️  Failed to fetch quiz from database:', dbError);
    }

    return null;
  } catch (error) {
    console.error('❌ Error retrieving cached quiz:', error);
    return null;
  }
};

/**
 * Get quiz from localStorage only
 */
export const getLocalQuiz = (articleId: string): QuizData | null => {
  try {
    const cache = getLocalQuizCache();
    const cached = cache.find(q => q.articleId === articleId);
    return cached?.quiz || null;
  } catch (error) {
    console.error('❌ Error reading quiz from localStorage:', error);
    return null;
  }
};

/**
 * Get all cached quizzes from localStorage
 */
export const getLocalQuizCache = (): CachedQuiz[] => {
  try {
    const data = localStorage.getItem(QUIZ_STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('❌ Error reading quiz cache from localStorage:', error);
    return [];
  }
};

/**
 * Sync quiz to database
 */
const syncQuizToDatabase = async (
  articleId: string,
  articleTitle: string,
  quiz: QuizData,
  sourceRoadmapId?: string,
  sourceRoadmapTitle?: string
): Promise<void> => {
  try {
    // Check if quiz exists (without .single() to avoid 406 error)
    const { data: existing, error: fetchError } = await supabase
      .from(QUIZ_TABLE)
      .select('id')
      .eq('article_id', articleId)
      .limit(1);

    // Handle different error codes
    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 is "not found" which is ok
      throw fetchError;
    }

    if (existing && existing.length > 0) {
      // Update existing quiz
      const { error: updateError } = await supabase
        .from(QUIZ_TABLE)
        .update({
          article_title: articleTitle,
          quiz_data: quiz,
          source_roadmap_id: sourceRoadmapId,
          source_roadmap_title: sourceRoadmapTitle,
          updated_at: new Date().toISOString()
        })
        .eq('article_id', articleId);

      if (updateError) throw updateError;
      console.log('✅ Quiz updated in database:', articleId);
    } else {
      // Insert new quiz
      const { error: insertError } = await supabase
        .from(QUIZ_TABLE)
        .insert({
          article_id: articleId,
          article_title: articleTitle,
          quiz_data: quiz,
          source_roadmap_id: sourceRoadmapId,
          source_roadmap_title: sourceRoadmapTitle,
          created_at: new Date().toISOString()
        });

      if (insertError) throw insertError;
      console.log('✅ Quiz inserted into database:', articleId);
    }
  } catch (error) {
    console.error('❌ Error syncing quiz to database:', error);
    throw error;
  }
};

/**
 * Get quiz from database
 */
const getQuizFromDatabase = async (articleId: string): Promise<QuizData | null> => {
  try {
    const { data, error } = await supabase
      .from(QUIZ_TABLE)
      .select('quiz_data')
      .eq('article_id', articleId)
      .limit(1);

    if (error) {
      throw error;
    }

    if (data && data.length > 0) {
      return data[0]?.quiz_data || null;
    }

    return null;
  } catch (error) {
    console.error('❌ Error fetching quiz from database:', error);
    throw error;
  }
};

/**
 * Get all quizzes for a roadmap
 */
export const getRoadmapQuizzes = async (roadmapId: string): Promise<CachedQuiz[]> => {
  try {
    // Try database first
    try {
      const { data, error } = await supabase
        .from(QUIZ_TABLE)
        .select('*')
        .eq('source_roadmap_id', roadmapId);

      if (!error && data) {
        return data.map(item => ({
          articleId: item.article_id,
          articleTitle: item.article_title,
          quiz: item.quiz_data,
          cachedAt: item.created_at,
          sourceRoadmapId: item.source_roadmap_id,
          sourceRoadmapTitle: item.source_roadmap_title
        }));
      }
    } catch (dbError) {
      console.warn('⚠️  Failed to fetch roadmap quizzes from database:', dbError);
    }

    // Fallback to localStorage
    const cache = getLocalQuizCache();
    return cache.filter(q => q.sourceRoadmapId === roadmapId);
  } catch (error) {
    console.error('❌ Error retrieving roadmap quizzes:', error);
    return [];
  }
};

/**
 * Delete cached quiz
 */
export const deleteCachedQuiz = async (articleId: string): Promise<void> => {
  try {
    // Delete from localStorage
    const cache = getLocalQuizCache();
    const filtered = cache.filter(q => q.articleId !== articleId);
    localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(filtered));
    console.log('✅ Quiz deleted from localStorage:', articleId);

    // Try to delete from database
    try {
      await supabase
        .from(QUIZ_TABLE)
        .delete()
        .eq('article_id', articleId);
      console.log('✅ Quiz deleted from database:', articleId);
    } catch (dbError) {
      console.warn('⚠️  Failed to delete quiz from database:', dbError);
    }
  } catch (error) {
    console.error('❌ Error deleting cached quiz:', error);
  }
};

/**
 * Clear all quizzes from cache
 */
export const clearQuizCache = (): void => {
  try {
    localStorage.removeItem(QUIZ_STORAGE_KEY);
    console.log('✅ Quiz cache cleared');
  } catch (error) {
    console.error('❌ Error clearing quiz cache:', error);
  }
};

/**
 * Get cache statistics
 */
export const getQuizCacheStats = (): {
  totalCached: number;
  lastUpdated: string | null;
  cacheSize: string;
} => {
  try {
    const cache = getLocalQuizCache();
    const cacheData = JSON.stringify(cache);
    const cacheSize = (new Blob([cacheData]).size / 1024).toFixed(2); // KB

    return {
      totalCached: cache.length,
      lastUpdated: cache[cache.length - 1]?.cachedAt || null,
      cacheSize: `${cacheSize} KB`
    };
  } catch (error) {
    console.error('❌ Error calculating cache stats:', error);
    return {
      totalCached: 0,
      lastUpdated: null,
      cacheSize: '0 KB'
    };
  }
};

/**
 * Sync all local quizzes to database (useful when coming back online)
 */
export const syncAllQuizzesToDatabase = async (): Promise<void> => {
  try {
    const cache = getLocalQuizCache();
    let synced = 0;
    let failed = 0;

    for (const item of cache) {
      try {
        await syncQuizToDatabase(
          item.articleId,
          item.articleTitle,
          item.quiz,
          item.sourceRoadmapId,
          item.sourceRoadmapTitle
        );
        synced++;
      } catch (error) {
        console.warn(`⚠️  Failed to sync quiz ${item.articleId}:`, error);
        failed++;
      }
    }

    console.log(`✅ Sync complete: ${synced} synced, ${failed} failed`);
  } catch (error) {
    console.error('❌ Error syncing quizzes:', error);
  }
};
